import { buildQuickHelpHTML } from './dom'
import { QUICK_HELP_STYLES } from './styles'
import type { BotWidgetConfig, ParentToQuickHelpMessage, QuickHelpParams } from './types'

const QUICK_HELP_STYLE_ID = 'quickhelp-styles'
const QUICK_HELP_IFRAME_ID = 'quick-help-bot-chat-iframe'

// Ported from the "#region QuickHelp Assistant (Support Genie)" block —
// the MCP "Tax Copilot" quick-fix side panel. It's a second, independent
// chat iframe from the main widget, opened programmatically by host-page
// code (e.g. `botWidget.quickHelp.open({ query, request_payload })`) rather
// than via the launcher button.
export class QuickHelpPanel {
  private container: HTMLDivElement | null = null
  private isOpen = false

  constructor(private config: BotWidgetConfig) {}

  updateConfig(config: BotWidgetConfig): void {
    this.config = config
  }

  open(params: QuickHelpParams = {}): void {
    // Merge params into config first (params may carry productKey/emailAddress
    // overrides), same as the original.
    this.config = { ...this.config, ...params } as BotWidgetConfig

    // NOTE: preserved exactly from the source — the iframe is (re)created
    // and (re)loaded only when isEnableQuickFixSupport is *falsy*. The name
    // reads like it should gate the opposite way; kept as-is since flipping
    // it would change which callers get a working panel with no way to
    // verify against the original's intent from the code alone.
    if (!this.config.isEnableQuickFixSupport) {
      if (!this.container) this.create()
      this.loadIframe()
    }

    const closeButton = this.container?.querySelector<HTMLButtonElement>('.quick-help-close')
    closeButton?.addEventListener('click', () => this.close())

    // The panel's own CSS (.quick-help-bot-chat-window / .open, in styles.ts) owns
    // showing and hiding entirely — no inline display/visibility overrides here — so
    // its transform/visibility transition actually gets to play on both open and
    // close instead of being short-circuited by a container-level display toggle.
    const panel = this.container?.querySelector<HTMLElement>('.quick-help-bot-chat-window')
    panel?.classList.add('open')
    this.isOpen = true

    setTimeout(() => {
      const message: ParentToQuickHelpMessage = {
        type: 'QuickFixSupport',
        message: params.query,
        request_payload: params.request_payload,
        jwt_token: params.request_payload?.jwt,
        isHideFixError: params.isHideFixError,
        actionName: params.actionName,
      }
      this.sendMessage(message)
    }, 1000)
  }

  close(): void {
    if (!this.container) return
    const panel = this.container.querySelector<HTMLElement>('.quick-help-bot-chat-window')
    panel?.classList.remove('open')
    this.isOpen = false
  }

  get opened(): boolean {
    return this.isOpen
  }

  private create(): void {
    this.container = document.createElement('div')
    this.container.innerHTML = buildQuickHelpHTML()

    if (!document.getElementById(QUICK_HELP_STYLE_ID)) {
      const style = document.createElement('style')
      style.id = QUICK_HELP_STYLE_ID
      style.textContent = QUICK_HELP_STYLES
      document.head.appendChild(style)
    }

    document.body.appendChild(this.container)
  }

  private loadIframe(): void {
    const iframe = this.container?.querySelector<HTMLIFrameElement>('.quick-help-bot-chat-iframe')
    if (!iframe) return

    const store = JSON.parse(localStorage.getItem('myStore') || localStorage.getItem('userInfo') || 'null')
    const jwt = store?.session?.JwtToken

    const userDetail = {
      EmailAddress: this.config.emailAddress,
      ContactName: this.config.contactName,
      Product: this.config.product,
      ProductId: this.config.productId,
      PageId: this.config.pageId,
      ProductKey: this.config.productKey,
      PartnerName: this.config.partnerName,
      PageUrl: location.href,
      IsHomeSite: this.config.isHomeSite,
      JwtToken: jwt,
      IsEnableQuickFixSupport: true,
    }

    iframe.setAttribute('importance', 'high')
    iframe.setAttribute('loading', 'eager')
    iframe.setAttribute('fetchpriority', 'high')
    iframe.src = `${this.config.botUrl}?UserDetail=${window.btoa(JSON.stringify(userDetail))}`
  }

  private sendMessage(message: ParentToQuickHelpMessage): void {
    const iframe = document.getElementById(QUICK_HELP_IFRAME_ID) as HTMLIFrameElement | null
    if (!iframe) return
    const payload = JSON.stringify({ ...message, iframeType: 'quick-help' })
    if (iframe.contentWindow) {
      iframe.contentWindow.postMessage(payload, this.config.botUrl)
    } else {
      iframe.onload = () => iframe.contentWindow?.postMessage(payload, this.config.botUrl)
    }
  }
}
